import React, { useEffect, useMemo, useRef, useState } from 'react';
import Player from '@vimeo/player';
import { generateRandomString } from 'shared/utils';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { VideoPlayerWrapper } from './VideoCard.styles';

type Props = {
  url?: string;
  height?: number;
  width?: number;
  onPlay?: () => void;
  onPlaying?: () => void;
};

export function VideoPlayer({ url, height, width, onPlay, onPlaying }: Props) {
  const [isLoading, setIsLoading] = useState(!!url);
  const videoId = useMemo(() => generateRandomString(), []);
  const playerRef = useRef<Player | null>(null);
  const onPlayRef = useRef(onPlay);
  const onPlayingRef = useRef(onPlaying);

  // Update refs when callbacks change
  useEffect(() => {
    onPlayRef.current = onPlay;
    onPlayingRef.current = onPlaying;
  }, [onPlay, onPlaying]);

  useEffect(() => {
    if (url && !playerRef.current) {
      const playerOptions = {
        url,
        height,
        width,
        loop: false,
      };
      playerRef.current = new Player(videoId, playerOptions);

      playerRef.current.ready().then(() => {
        setIsLoading(false);
      });

      playerRef.current.on('play', () => {
        onPlayRef.current?.();
      });

      playerRef.current.on('playing', () => {
        onPlayingRef.current?.();
      });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [url, height, width, videoId]);

  if (!url) return null;

  return (
    <VideoPlayerWrapper id={videoId}>
      <LoadingSpinner localCenter isLoading={isLoading} />
    </VideoPlayerWrapper>
  );
}

export default VideoPlayer;
